import ApiCache, { ApiCacheOptions, apiCaches } from "./apiCache"
import api from "./api"
import { DataDefinitionWithFields, DataSource, DataSourceItem, TemplateDetails } from "@/types"
import { CurrentUser, UserIdWithStatusAndFields } from "@/types/user"
import { InsightPreset } from "@/types/insights/presets/insightPreset"

/**
 * Caches frequently used API requests for the default api service.
 */
class ApiDefaultCache extends ApiCache {
	protected cacheName = "ApiDefaultCache"

	// #region Standard API Methods

	public async templateDetails (templateId: string): ReturnType<typeof api.templateDetails> {
		const methodName = "templateDetails"
		const fetchCallFn = () => api.templateDetails(templateId)

		const dataPromise = this.getOrFetchAndStore(methodName, { templateId }, fetchCallFn)
		return dataPromise as Promise<TemplateDetails>
	}

	public async dataSourceGet (dataSourceId: string): ReturnType<typeof api.dataSourceGet> {
		const methodName = "dataSourceGet"
		const fetchCallFn = () => api.dataSourceGet(dataSourceId)

		const dataPromise = this.getOrFetchAndStore(methodName, { dataSourceId }, fetchCallFn)
		return dataPromise as Promise<DataSource>
	}

	public async definitionDetails (definitionId: string): ReturnType<typeof api.definitionDetails> {
		const methodName = "definitionDetails"
		const fetchCallFn = () => api.definitionDetails(definitionId)

		const dataPromise = this.getOrFetchAndStore(methodName, { definitionId }, fetchCallFn)
		return dataPromise as Promise<DataDefinitionWithFields>
	}

	public async dataSourceGetAll (dataSourceId: string, getAll: boolean): ReturnType<typeof api.dataSourceGetAll> {
		const methodName = "dataSourceGetAll"
		const fetchCallFn = () => api.dataSourceGetAll(dataSourceId, getAll)

		const dataPromise = this.getOrFetchAndStore(methodName, { dataSourceId, getAll }, fetchCallFn)
		return dataPromise as Promise<DataSourceItem[]>
	}

	public async me (cacheOptions?: ApiCacheOptions): ReturnType<typeof api.me> {
		const methodName = "me"
		const fetchCallFn = () => api.me()
		if (!cacheOptions) cacheOptions = { cacheTimeMins: 0.1 } // Short cache duration but prevents duplicate requests.

		const dataPromise = this.getOrFetchAndStore(methodName, {}, fetchCallFn, cacheOptions)
		return dataPromise as Promise<CurrentUser>
	}

	public async userDetails (userId: string): ReturnType<typeof api.userDetails> {
		const methodName = "userDetails"
		const fetchCallFn = () => api.userDetails(userId)

		const dataPromise = this.getOrFetchAndStore(methodName, { userId }, fetchCallFn)
		return dataPromise as Promise<CurrentUser>
	}

	public async insightEnabledPresetsGet (): ReturnType<typeof api.insightEnabledPresetsGet> {
		const methodName = "insightEnabledPresetsGet"
		const fetchCallFn = () => api.insightEnabledPresetsGet()

		const dataPromise = this.getOrFetchAndStore(methodName, {}, fetchCallFn)
		return dataPromise as Promise<InsightPreset[]>
	}

	// #endregion

	// #region Specialized Methods

	public async columnValuesForField (fieldId: string, columnValuesFn: (apiInstance: typeof api) => Promise<unknown>): Promise<unknown> {
		const methodName = "columnValuesForField"
		const fetchCallFn = () => columnValuesFn(api)

		const dataPromise = this.getOrFetchAndStore(methodName, { fieldId }, fetchCallFn)
		return dataPromise as Promise<unknown>
	}

	private userStatusWithFieldsCache: Map<string, { value: Promise<UserIdWithStatusAndFields>, expiry: Date }> | undefined = undefined
	/**
	 * Gets the userStatusListWithFields for a list of user IDs. Caches results for each user ID.
	 * @param userIds The list of user IDs to get the userStatusListWithFields for.
	 * @returns A promise that resolves to an array of UserIdWithStatusAndFields objects.
	 */
	public async userStatusListWithFieldsGet (userIds: string[]): ReturnType<typeof api.userStatusListWithFieldsGet> {
		const methodName = "userStatusListWithFieldsGet"

		if (this.skipCache) {
			this.logDebugMessage("Skip cache is enabled, skipping userStatusListWithFieldsGet")
			return api.userStatusListWithFieldsGet(userIds) as Promise<UserIdWithStatusAndFields[]>
		}

		// Initialize the cache and add cleanup and clear actions if needed.

		if (!this.userStatusWithFieldsCache) {
			this.userStatusWithFieldsCache = new Map<string, { value: Promise<UserIdWithStatusAndFields>, expiry: Date }>()

			this.cleanupAdditionalActions.push(() => {
				if (!this.userStatusWithFieldsCache) return

				const now = new Date(Date.now())
				const deletedValues: string[] = []

				Array.from(this.userStatusWithFieldsCache.keys()).forEach(userId => {
					const cachedValue = this.userStatusWithFieldsCache!.get(userId)
					if (cachedValue && cachedValue.expiry <= now) {
						this.userStatusWithFieldsCache!.delete(userId)
						deletedValues.push(userId)
					}
				})

				if (deletedValues.length > 0) {
					this.logDebugMessage(`Deleted expired userStatusListWithFields values for ${deletedValues.length} user(s)`)
				}
			})

			this.clearAdditionalActions.push(() => {
				if (!this.userStatusWithFieldsCache) return

				this.userStatusWithFieldsCache!.clear()
				this.logDebugMessage("Cleared userStatusListWithFields cache")
			})
		}

		// Determine what's already in the cache (including those in-progress) and what needs to be fetched.

		const existingValues: Promise<UserIdWithStatusAndFields>[] = []
		const userIdsToFetch: Set<string> = new Set<string>()
		userIds.forEach(async userId => {
			if (!userId) return

			if (this.userStatusWithFieldsCache!.has(userId)) {
				const cachedValue = this.userStatusWithFieldsCache!.get(userId)
				if (cachedValue && cachedValue.expiry > new Date(Date.now())) {
					existingValues.push(cachedValue.value)
					return
				}
			}

			userIdsToFetch.add(userId)
		})

		this.logDebugMessage(`Fetching ${userIdsToFetch.size} userStatusListWithFields values, ${existingValues.length} retrieved from cache`)

		// eslint-disable-next-line func-call-spacing
		const fetchPromiseResolves = new Map<string, (value: UserIdWithStatusAndFields | PromiseLike<UserIdWithStatusAndFields>) => void>()

		if (userIdsToFetch.size === 0) return Promise.all(existingValues)

		const userIdsToFetchArray = Array.from(userIdsToFetch.values())

		// Create an entry and promise for each user ID that is not in the cache.
		userIdsToFetchArray.forEach(userId => {
			if (!this.userStatusWithFieldsCache!.has(userId)) {
				this.userStatusWithFieldsCache!.set(userId, {
					value: new Promise<UserIdWithStatusAndFields>(async (resolve, reject) => {
						fetchPromiseResolves.set(userId, resolve)
					}),
					expiry: new Date(Date.now() + this.cacheTimeMs)
				})
			}
		})

		const paramsJson = this.getParamsJson({ userIds: userIdsToFetchArray })
		const fetchCallFn = () => api.userStatusListWithFieldsGet(userIdsToFetchArray)

		// Fetch for the user IDs that are not in the cache.
		const fetchedValues = await this.fetch(methodName, paramsJson, fetchCallFn) as UserIdWithStatusAndFields[]

		// Update the cache with the fetched values and resolve the promises.
		fetchedValues.forEach(userStatus => {
			const userId = userStatus.userId
			const userIdResolve = fetchPromiseResolves.get(userId)
			if (userIdResolve) userIdResolve(userStatus)

			const cachedValue = this.userStatusWithFieldsCache!.get(userId)
			if (cachedValue) {
				cachedValue.expiry = new Date(Date.now() + this.cacheTimeMs)
			}
		})

		// Ensure that everything is resolved.
		const completedExistingValues = await Promise.all(existingValues)
		return completedExistingValues.concat(fetchedValues)
	}

	// #endregion
}

const apiDefaultCache = new ApiDefaultCache()
export default apiDefaultCache

// Register the cache in the global API Cache list.
apiCaches.push(apiDefaultCache)
